<template>
  <div>
    <!-- Row -->
    <div class="d-flex align-center justify-space-between meal-header-row" style="width: 100%;">
      <div>{{ child.lastName }} {{ child.firstName }}</div>
      <div v-if="meal">
        <v-btn variant="text" color="primary" @click="fullEat" :disabled="fullBtnLoading">{{ $t('meal.allFinished') }}</v-btn>
      </div>
    </div>
    <template v-if="meal">
      <v-row>
        <v-col cols="4" class="pr-0">
          <EatingStatusItem
            :text="$t('meal.stapleFood')"
            field="stapleFood"
            :item="meal.stapleFood"
            @changeStatusItem="changeStatusItem"
          />
        </v-col>
        <v-col cols="4" class="pl-0 pr-0">
          <EatingStatusItem
            :text="$t('meal.mainDish')"
            field="mainDish"
            :item="meal.mainDish"
            @changeStatusItem="changeStatusItem"
          />
        </v-col>
        <v-col cols="4" class="pl-0">
          <EatingStatusItem
            :text="$t('meal.sideDish')"
            field="sideDish"
            :item="meal.sideDish"
            @changeStatusItem="changeStatusItem"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="4" class="pr-0">
          <EatingStatusItem
            :text="$t('meal.soup')"
            field="soup"
            :item="meal.soup"
            @changeStatusItem="changeStatusItem"
          />
        </v-col>
        <v-col cols="4" class="pl-0 pr-0">
          <EatingStatusItem
            :text="$t('meal.dessert')"
            field="dessert"
            :item="meal.dessert"
            @changeStatusItem="changeStatusItem"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="4" class="pr-0">
          <EatingStatusItem
            :text="$t('meal.milk')"
            field="milk"
            :item="meal.milk"
            @changeStatusItem="changeStatusItem"
          />
        </v-col>
        <v-col cols="4" class="pl-0 pr-0">
          <EatingStatusItem
            :text="$t('meal.snack')"
            field="snack"
            :item="meal.snack"
            @changeStatusItem="changeStatusItem"
          />
        </v-col>
      </v-row>

      <v-text-field
        variant="underlined"
        single-line rows="1"
        hide-details
        density="compact"
        :placeholder="$t('comment.placeholder')"
        prepend-inner-icon="mdi-note-outline"
        v-model="inputComment"
        :loading="btnLoading"
        color="primary"
      >
        <template v-slot:append>
          <v-icon
            v-if="inputComment"
            @click="sendMessage"
            color="primary"
          >
            mdi-send
          </v-icon>
        </template>
      </v-text-field>
    </template>
    <template v-else>
      <div class="d-flex flex-column align-center ma-3">
        <v-btn
          color="primary"
          outlined
          @click="createMealReport(child.childId)"
          >{{ $t('records.addRecord') }}
        </v-btn>
      </div>
    </template>
  </div>
</template>
<script lang="ts">
import Vue, { PropType, defineComponent } from 'vue';
import { MealResponse, NurseryChildResponse, MealStatus, MealStatusType } from 'chaild-api/lib';
import EatingStatusItem from '@/components/molecules/EatingStatusItem.vue';

import { when } from '@/service/Switch';

type MealField =
  | 'stapleFood'
  | 'mainDish'
  | 'sideDish'
  | 'soup'
  | 'dessert'
  | 'milk'
  | 'snack';

export default defineComponent({
  components: {
    EatingStatusItem,
  },
  props: {
    classMealReport: {
      type: Array as PropType<MealResponse[]>,
      required: true,
      default: [],
    },
    child: {
      type: Object as PropType<NurseryChildResponse>,
      required: true,
    },
    fullBtnLoading: {
      type: Boolean,
      default: false,
    },
    btnLoading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => {
    return {
      inputComment: '',
    };
  },
  computed: {
    meal(): MealResponse | null {
      if (this.classMealReport.length > 0) {
        const find = this.classMealReport.find(meal => meal.childId === this.child.childId);
        if (find) {
          this.inputComment = find.comment;
          return find;
        }
      }
      return null;
    },
  },
  methods: {
    sendMessage() {
      if (this.meal) {
        const input = {
          childId: this.child.childId,
          mealId: this.meal.mealId,
          stapleFood: this.meal.stapleFood,
          mainDish: this.meal.mainDish,
          sideDish: this.meal.sideDish,
          soup: this.meal.soup,
          dessert: this.meal.dessert,
          milk: this.meal.milk,
          snack: this.meal.snack,
          comment: this.inputComment,
        };
        this.$emit('sendMessage', input);
      }
    },
    changeStatusItem(status: MealStatusType, field: MealField) {
      if (!this.meal) {
        return;
      }

      const input = {
        childId: this.child.childId,
        mealId: this.meal.mealId,
        stapleFood: this.meal.stapleFood,
        mainDish: this.meal.mainDish,
        sideDish: this.meal.sideDish,
        soup: this.meal.soup,
        dessert: this.meal.dessert,
        milk: this.meal.milk,
        snack: this.meal.snack,
      };

      input[field] = status;

      this.$emit('changeStatusItem', input);
    },
    createMealReport(childId: number) {
      this.$emit('createMealReport', childId);
    },
    fullEat() {
      this.$emit('fullEat', this.meal);
    },
    showMealStatus(status: MealStatusType): string {
      const temp = when(status)
        .on(
          v => v === MealStatus.refill,
          () => 'refill'
        )
        .on(
          v => v === MealStatus.finished,
          () => 'finished'
        )
        .on(
          v => v === MealStatus.unfinished,
          () => 'unfinished'
        )
        .on(
          v => v === MealStatus.half,
          () => 'half'
        )
        .on(
          v => v === MealStatus.little,
          () => 'little'
        )
        .on(
          v => v === MealStatus.notEat,
          () => 'notEat'
        )
        .on(
          v => v === MealStatus.notServed,
          () => 'notServed'
        )
        .otherwise(() => '');
      return temp;
    },
  },
  mounted: async function() {},
});
</script>
<style lang="scss" scoped>
.meal-header-row {
  font-size: 1rem;
}

.circle-back-noEat,
.circle-back-little,
.circle-back-half,
.circle-back-unfinished,
.circle-back-finished,
.circle-back-refill {
  height: 34px;
  width: 34px;
  border-radius: 100%;
  background-color: rgb(var(--v-theme-primary));
}
.circle-back-notServed {
  height: 34px;
  width: 34px;
  background-color: white;
}
.circle-front-notEat {
  height: 30px;
  width: 30px;
  top: 2px;
  left: 2px;
  border-radius: 100%;
  background-color: white;
  position: absolute;
}

.circle-front-little {
  height: 22px;
  width: 30px;
  top: 2px;
  left: 2px;
  border-radius: 22px 22px 5px 5px;
  background-color: white;
  position: absolute;
}
.circle-front-half {
  height: 15px;
  width: 30px;
  top: 2px;
  left: 2px;
  border-radius: 15px 15px 0px 0px;
  background-color: white;
  position: absolute;
}

.circle-front-unfinished {
  height: 15px;
  width: 30px;
  top: 2px;
  left: 2px;
  border-radius: 15px 15px 0px 0px;
  background-color: white;
  position: absolute;
  // border-top: 3px solid #ff6c00;
  // border-right: 1px solid #ff6c00;
  border-bottom: 7px solid rgb(var(--v-theme-primary));
  // border-left: 1px solid #ff6c00;
}

.circle-front-finished {
  height: 30px;
  width: 30px;
  top: 1px;
  left: 1px;
  border-radius: 100%;
  background-color: rgb(var(--v-theme-primary));
  position: absolute;
}

.circle-front-refill {
  height: 34px;
  width: 34px;
  left: 7px;
  border-radius: 100%;
  background-color: rgb(var(--v-theme-primary));
  border: 1px solid white;
  position: absolute;
}

.circle-front-notServed {
  height: 5px;
  width: 34px;
  top: 17px;
  background-color: #c4c4c4;
  position: absolute;
}
</style>
